import { useMemo, useState, useCallback } from 'react';
import { Button } from "@datassential/platform-ui-lib";
import useByoSettings, { ByoSettingInput } from '@apiHooks/useByoSettings';
import { ExportRequestData } from '@apiHooks/useExport';
import { useDefaultFilterData, useFilters, useMappedFiltersToApi } from '@vms/SelectFilters/SelectFiltersContext';
import { getByoSettingsUrl, Routes } from "@core/routes";
import ConfirmationPopup from "@components/ConfirmationPopup/ConfirmationPopup";
import SaveByoSettingsPopup from "@components/SaveByoSettingsPopup/SaveByoSettingsPopup";
import { useByoDataContext } from '../../ByoContext';
import FooterMenuDropdown from './FooterMenuDropdown';

type ReportModel = Omit<ByoSettingInput, 'words' | 'filters'> & {
    isNewReport?: boolean;
};

const FooterContent = () => {
    const { data: words, byoSettings } = useByoDataContext();
    const { countryCode } = useFilters();
    const defaultFilterData = useDefaultFilterData();
    const { createSetting, updateSetting } = useByoSettings();

    const [isClearOpened, setClearOpened] = useState(false);
    const [isSaveOpened, setSaveOpened] = useState(false);

    const mapFiltersToApi = useMappedFiltersToApi();

    const keywords = useMemo(() => words.map(d => d.keyword), [words]);

    const exportData = useMemo<ExportRequestData>(() => ({
        customerFilter: {
            mainFilter: { filters: mapFiltersToApi, words: keywords },
        },
        fullFilter: { ...defaultFilterData, countryCode } as any,
    }), [mapFiltersToApi, keywords, defaultFilterData, countryCode]);

    const handleSave = useCallback(async (reportModel: ReportModel) => {
        const { isNewReport, ...model } = reportModel;

        let { id } = model;

        const data: ByoSettingInput = {
            ...model,
            words: keywords,
            filters: mapFiltersToApi,
        };

        if (isNewReport) {
            const response = await createSetting(data);
            if (response !== 0) {
                id = response;
            }
        }
        else {
            await updateSetting(data);
        }

        window.location.href = getByoSettingsUrl(id, keywords.length);
    }, [keywords, mapFiltersToApi, createSetting, updateSetting]);

    const handleClear = useCallback(() => {
        window.location.href = Routes.byo;
    }, []);

    return (
        <>
            <Button
                className="page-footer__btn is-secondary"
                onClick={() => setClearOpened(true)}
                isDisabled={words.length === 0}
            >
                Clear
            </Button>
            <Button
                className="page-footer__btn is-secondary"
                onClick={() => setSaveOpened(true)}
                isDisabled={words.length === 0}
            >
                Save
            </Button>
            <FooterMenuDropdown
                exportData={exportData}
                isDisabled={words.length === 0}
            />
            <ConfirmationPopup
                closeModal={() => setClearOpened(false)}
                shown={isClearOpened}
                title="Clear keywords and filters"
                // @ts-ignore
                text="Are you sure you want to clear all keywords and filters?"
                onConfirm={handleClear}
            />
            <SaveByoSettingsPopup
                shown={isSaveOpened}
                closeModal={() => setSaveOpened(false)}
                currentByoSetting={byoSettings}
                onSave={handleSave}
            />
        </>
    );
};

export default FooterContent;